import React from "react";
import { Eye, User, Sparkles, Home } from "lucide-react";
import { BACKEND_URL } from "../lib/api";

/**
 * PreviewModeEntry — shared "Explore TREL without signing in" panel.
 *
 * Surfaces 3 read-only preview sessions (Customer / Concierge / Host) and
 * is rendered on BOTH the Login + Signup pages. The exact copy differs by
 * surface — new visitors land on Signup (the primary preview entry per
 * Phase 33), returning users hit Login. Same 3 buttons + same backend
 * `GET /api/demo/enter/{role}` endpoint behind the scenes — only the
 * supporting copy + button subtitles change.
 *
 * Props:
 *   variant: "signup" | "login" — controls the title + supporting copy +
 *            button subtitles. Default "signup" (the primary entry).
 *   testidPrefix: optional override; defaults to "preview-entry".
 *
 * The actual session creation flow is unchanged: clicking a role calls
 * `GET ${BACKEND_URL}/api/demo/enter/{role}` which sets the preview JWT
 * cookie + redirects to the matching dashboard. Read-only — see the
 * preview write-guard middleware in `backend/app/main` for enforcement.
 */
const PREVIEW_COPY = {
  signup: {
    title: "Explore TREL Before Joining",
    supporting:
      "Take a guided preview of TREL and see how customers, Concierges, and Hosts create unforgettable experiences before creating your account.",
    options: [
      {
        role: "customer",
        label: "Preview Customer Experience",
        icon: User,
        note: "Discover AI planning, Concierge matching, bookings, and saved experiences.",
      },
      {
        role: "provider",
        label: "Preview Concierge Experience",
        icon: Sparkles,
        note: "Explore requests, offers, earnings, availability, and business tools.",
      },
      {
        role: "host",
        label: "Preview Host Experience",
        icon: Home,
        note: "See how properties add Concierge services and create guest experiences.",
      },
    ],
  },
  login: {
    title: "Explore TREL in demo mode without signing in.",
    supporting:
      "Preview dashboards and workflows using sample data. Bookings, payments, messages, and account changes are disabled.",
    options: [
      {
        role: "customer",
        label: "Preview Customer Experience",
        icon: User,
        note: "Discover AI planning, Concierge matching, bookings, and saved experiences.",
      },
      {
        role: "provider",
        label: "Preview Concierge Experience",
        icon: Sparkles,
        note: "Explore requests, offers, earnings, availability, and business tools.",
      },
      {
        role: "host",
        label: "Preview Host Experience",
        icon: Home,
        note: "See how properties add Concierge services and create guest experiences.",
      },
    ],
  },
};

export default function PreviewModeEntry({ variant = "signup", testidPrefix = "preview-entry" }) {
  const copy = PREVIEW_COPY[variant] || PREVIEW_COPY.signup;
  return (
    <div
      className="mt-10 rounded-3xl card-luxury-elevated p-6"
      data-testid={`${testidPrefix}-panel`}
    >
      <div className="flex items-center gap-2">
        <Eye size={12} className="text-[#064E3B]"/>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">
          TREL Preview Mode
        </div>
      </div>
      <h2
        className="font-serif mt-2 leading-tight text-[#071A2D]"
        style={{ fontSize: "clamp(1.15rem, 2vw, 1.4rem)" }}
        data-testid={`${testidPrefix}-title`}
      >
        {copy.title}
      </h2>
      <p
        className="text-xs sm:text-sm text-[#071A2D]/70 mt-2 leading-relaxed"
        data-testid={`${testidPrefix}-supporting`}
      >
        {copy.supporting}
      </p>
      <div className="mt-5 grid grid-cols-1 gap-2.5">
        {copy.options.map(({ role, label, icon: Icon, note }) => (
          <a
            key={role}
            data-testid={`${testidPrefix}-${role}`}
            href={`${BACKEND_URL}/api/demo/enter/${role}`}
            className="group flex items-center gap-3 w-full px-4 py-3 rounded-2xl card-luxury-well hover:bg-[#064E3B] hover:text-[#FFF8EE] transition-colors text-left no-underline"
          >
            <span className="w-8 h-8 rounded-full bg-[#064E3B]/10 group-hover:bg-[#FFF8EE]/15 flex items-center justify-center shrink-0">
              <Icon size={15}/>
            </span>
            <span className="flex-1">
              <span className="block text-sm font-medium">{label}</span>
              <span className="block text-[11px] text-[#071A2D]/55 group-hover:text-[#FFF8EE]/75">{note}</span>
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}
